import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import EmailSignupForm from '../brand/EmailSignupForm';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import heroStill from '../../assets/images/home/hero-still.jpg';
import maskArch from '../../assets/images/home/mask-arch.svg';
import maskCircle from '../../assets/images/home/mask-circle.svg';
import avatar1 from '../../assets/images/home/avatar-1.jpg';
import avatar2 from '../../assets/images/home/avatar-2.jpg';
import avatar3 from '../../assets/images/home/avatar-3.jpg';

const HERO_VIDEO_SRC = '/videos/hero.mp4';

const Wrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(24px, 12vh, 164px);
  gap: clamp(24px, 12svh, 164px);
  min-height: calc(100vh - var(--header-height, 56px));
  min-height: calc(100svh - var(--header-height, 56px));
  padding: 9vh var(--b-gutter) 5vh;
  padding: 9svh var(--b-gutter) 5svh;
  overflow: hidden;
  background-color: #d0d0d0;
  color: var(--b-white);
  text-align: center;

  > * {
    position: relative;
    z-index: 1;
  }

  ${media.large`
    gap: clamp(64px, 18vh, 164px);
    min-height: max(640px, calc(100vh - var(--header-height, 56px)));
    padding: 12vh var(--b-gutter) 6vh;
  `};

  &.still {
    background-color: var(--b-black);

    ${media.large`
      gap: clamp(64px, 14vh, 142px);
    `};
  }
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;

  img,
  video {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 148%;
    min-width: 2133px;
    height: auto;
    min-height: 100%;
    transform: translate(-50%, -50%);
    object-fit: cover;
    filter: blur(34px);
  }

  ${media.large`
    img,
    video {
      min-width: 0;
      width: 148%;
      height: 133%;
    }
  `};

  &.still {
    img {
      top: -10px;
      left: -10px;
      width: calc(100% + 20px);
      min-width: 0;
      height: calc(100% + 20px);
      transform: none;
      filter: blur(10px);
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.7);
    }
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
`;

const Headline = styled.h1`
  ${type('display', 'large', { emphasis: true, uppercase: true })};
  width: 100%;
  margin: 0;
  padding: 0;
  color: var(--b-white);
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  max-width: 595px;
`;

const Proof = styled.div`
  ${type('body', 'medium')};
  display: flex;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.67);
  text-align: left;

  .separator {
    display: block;
    height: 0;
    overflow: hidden;

    ${media.small`
      display: inline;
      height: auto;
    `};
  }
`;

const Avatars = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 80px;
  height: 32px;

  span {
    position: absolute;
    top: 0;
    width: 32px;
    height: 32px;
    overflow: hidden;
    background: #f1f1f1;
    mask: url(${maskArch}) no-repeat 0 0 / 26.586px 32px;

    &:nth-child(1) {
      left: 0;
    }

    &:nth-child(2) {
      left: 24px;
    }

    &:nth-child(3) {
      left: 48px;
      mask: url(${maskCircle}) no-repeat 0 0 / 32px 32px;
    }
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Hero = ({ headline, subheadline, cta, still }) => (
  <Wrapper className={still ? 'still' : undefined}>
    <Background aria-hidden="true" className={still ? 'still' : undefined}>
      {still && <img src={still} alt="" />}
      {!still && HERO_VIDEO_SRC && (
        <video src={HERO_VIDEO_SRC} poster={heroStill} autoPlay muted loop playsInline />
      )}
      {!still && !HERO_VIDEO_SRC && <img src={heroStill} alt="" />}
    </Background>
    <Copy>
      <Headline>
        {headline || (
          <FormattedMessage
            id="home.hero.headline"
            defaultMessage="Streaming, made {br}for classical music."
            values={{
              br: <br />,
            }}
          />
        )}
      </Headline>
      {subheadline}
    </Copy>
    <Details>
      <Proof>
        <Avatars aria-hidden="true">
          <span>
            <img src={avatar1} alt="Yuja Wang" />
          </span>
          <span>
            <img src={avatar2} alt="Thomas Hampson" />
          </span>
          <span>
            <img src={avatar3} alt="Chloe Chua" />
          </span>
        </Avatars>
        <span>
          <FormattedMessage
            id="home.hero.proof"
            defaultMessage="Built for classical since 2015{separator}2.5M+ Downloads"
            values={{ separator: <span className="separator"> · </span> }}
          />
        </span>
      </Proof>
      {cta || <EmailSignupForm from="Hero" />}
    </Details>
  </Wrapper>
);

Hero.propTypes = {
  headline: PropTypes.node,
  subheadline: PropTypes.node,
  cta: PropTypes.node,
  still: PropTypes.string,
};

export default Hero;



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Hero.js